import React, { useState } from 'react';
import { X, Eye, AlertCircle, Image as ImageIcon, Trash2, RefreshCw, Loader } from 'lucide-react';
import toast from 'react-hot-toast';
import LazyImage from './LazyImage';
import { preloadImages } from '../utils/imageCache';
import { getAbsoluteImageUrl, resolveImageUrl, addCacheBuster } from '../utils/urlUtils';

const AssetImagePreview = ({
  images = [],
  onImageRemove,
  onImageView,
  onImageRetry,
  editable = false,
  className = ''
}) => {
  const [loadingStates, setLoadingStates] = useState({});
  const [errorStates, setErrorStates] = useState({});
  const [retryAttempts, setRetryAttempts] = useState({});
  const [fallbackAttempts, setFallbackAttempts] = useState({});
  const [currentUrls, setCurrentUrls] = useState({});
  const [loadingTimeouts, setLoadingTimeouts] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [preloadingImages, setPreloadingImages] = useState(false);
  const [preloadProgress, setPreloadProgress] = useState({ loaded: 0, total: 0 });

  // Initialize fallback URLs for each image
  React.useEffect(() => {
    const newCurrentUrls = {};
    images.forEach(image => {
      if (!currentUrls[image.id]) {
        const originalUrl = image.url || image.preview;
        const urlResolution = resolveImageUrl(originalUrl, { 
          generateFallbacks: true, 
          addCacheBuster: false 
        });
        newCurrentUrls[image.id] = {
          primary: urlResolution.primary,
          fallbacks: urlResolution.fallbacks,
          current: urlResolution.primary,
          attemptIndex: 0
        };
      }
    });
    
    if (Object.keys(newCurrentUrls).length > 0) {
      setCurrentUrls(prev => ({ ...prev, ...newCurrentUrls }));
    }
  }, [images]);

  // Preload images for better performance
// Add this state to track if preloading is complete for the current image set
const [preloadedImageIds, setPreloadedImageIds] = useState([]);

// Modify your useEffect
React.useEffect(() => {
  // Get current image IDs
  const currentImageIds = images.map(img => img.id).sort().join(',');
  
  // Only preload if we haven't already preloaded these exact images
  if (images.length > 0 && 
      !preloadingImages && 
      Object.keys(currentUrls).length > 0 &&
      !preloadedImageIds.includes(currentImageIds)) {
    
    setPreloadingImages(true);
    setPreloadProgress({ loaded: 0, total: images.length });

    const imageUrls = images
      .map(img => currentUrls[img.id]?.current)
      .filter(Boolean);

    if (imageUrls.length > 0) {
      preloadImages(imageUrls, {
        batchSize: 2,
        onProgress: (loaded, total) => {
          setPreloadProgress({ loaded, total });
          console.log(`Preloaded ${loaded}/${total} images`);
        }
      }).then(() => {
        setPreloadingImages(false);
        setPreloadProgress({ loaded: 0, total: 0 });
        // Mark these images as preloaded
        setPreloadedImageIds(prev => [...prev, currentImageIds]);
      }).catch(error => {
        console.error('Error preloading images:', error);
        setPreloadingImages(false);
        setPreloadProgress({ loaded: 0, total: 0 });
      });
    } else {
      setPreloadingImages(false);
      setPreloadProgress({ loaded: 0, total: 0 });
      // Mark as preloaded even if no URLs to preload
      setPreloadedImageIds(prev => [...prev, currentImageIds]);
    }
  }
}, [images, preloadingImages, currentUrls, preloadedImageIds]);


  // Cleanup timeouts on unmount
  React.useEffect(() => {
    return () => {
      Object.values(loadingTimeouts).forEach(timeout => {
        if (timeout) clearTimeout(timeout);
      });
    };
  }, [loadingTimeouts]);

  const handleImageLoad = (imageId) => {
    // Clear any existing timeout for this image
    if (loadingTimeouts[imageId]) {
      clearTimeout(loadingTimeouts[imageId]);
      setLoadingTimeouts(prev => {
        const newTimeouts = { ...prev };
        delete newTimeouts[imageId];
        return newTimeouts;
      });
    }
    
    setLoadingStates(prev => ({ ...prev, [imageId]: false }));
    setErrorStates(prev => ({ ...prev, [imageId]: false }));
  };

  const tryNextFallbackUrl = (imageId) => {
    const urlData = currentUrls[imageId];
    if (!urlData) return false;

    const nextIndex = urlData.attemptIndex + 1;
    const allUrls = [urlData.primary, ...urlData.fallbacks];
    
    if (nextIndex < allUrls.length) {
      const nextUrl = allUrls[nextIndex];
      console.log(`Trying fallback URL ${nextIndex + 1}/${allUrls.length} for image ${imageId}:`, nextUrl);
      
      setCurrentUrls(prev => ({
        ...prev,
        [imageId]: {
          ...urlData,
          current: nextUrl,
          attemptIndex: nextIndex
        }
      }));
      
      setFallbackAttempts(prev => ({
        ...prev,
        [imageId]: (prev[imageId] || 0) + 1
      }));
      
      return true;
    }
    
    return false;
  };

  const handleImageError = (imageId, errorDetails = {}) => {
    console.log(`Image error for ${imageId}:`, errorDetails);
    
    // Try next fallback URL before showing error state
    const hasMoreFallbacks = tryNextFallbackUrl(imageId);
    
    if (hasMoreFallbacks) {
      // Keep loading state active while trying fallback
      console.log(`Attempting fallback URL for image ${imageId}`);
      return;
    }
    
    // All URLs exhausted, show error state
    setLoadingStates(prev => ({ ...prev, [imageId]: false }));
    setErrorStates(prev => ({ ...prev, [imageId]: true }));
    
    const image = images.find(img => img.id === imageId);
    const imageName = image?.filename || image?.original_filename || imageId;
    console.error(`All fallback URLs exhausted for image: ${imageName}`, errorDetails);
  };

  const handleImageLoadStart = (imageId) => {
    // Clear any existing timeout for this image
    if (loadingTimeouts[imageId]) {
      clearTimeout(loadingTimeouts[imageId]);
    }
    
    setLoadingStates(prev => ({ ...prev, [imageId]: true }));
    setErrorStates(prev => ({ ...prev, [imageId]: false }));
    
    // Set a timeout to prevent infinite loading states
    const timeout = setTimeout(() => {
      console.warn(`Loading timeout for image ${imageId}`);
      setLoadingStates(prev => ({ ...prev, [imageId]: false }));
      setErrorStates(prev => ({ ...prev, [imageId]: true }));
    }, 30000); // 30 second timeout
    
    setLoadingTimeouts(prev => ({ ...prev, [imageId]: timeout }));
  };

  const handleRemoveClick = (imageId, imageName) => {
    setShowDeleteConfirm({ id: imageId, name: imageName });
  };

  const confirmRemove = () => {
    if (showDeleteConfirm && onImageRemove) {
      onImageRemove(showDeleteConfirm.id);
      setShowDeleteConfirm(null);
    }
  };

  const cancelRemove = () => {
    setShowDeleteConfirm(null);
  };

  const handleViewImage = (imageIndex) => {
    if (onImageView) {
      onImageView(imageIndex);
    }
  };

  const handleRetryImage = async (imageId) => {
    const image = images.find(img => img.id === imageId);
    if (!image) return;

    const currentAttempts = retryAttempts[imageId] || 0;
    if (currentAttempts >= 3) {
      toast.error(`Maximum retry attempts reached for ${image.filename || image.original_filename}`);
      return;
    }

    setRetryAttempts(prev => ({ ...prev, [imageId]: currentAttempts + 1 }));
    setLoadingStates(prev => ({ ...prev, [imageId]: true }));
    setErrorStates(prev => ({ ...prev, [imageId]: false }));

    try {
      if (onImageRetry) {
        await onImageRetry(imageId);
        toast.success(`Successfully reloaded ${image.filename || image.original_filename}`);
      } else {
        // Reset fallback attempts and regenerate URLs with cache busting
        const originalUrl = image.url || image.preview;
        const urlResolution = resolveImageUrl(originalUrl, { 
          generateFallbacks: true, 
          addCacheBuster: true // Add cache buster for retry attempts
        });
        
        // Reset to primary URL with cache buster
        setCurrentUrls(prev => ({
          ...prev,
          [imageId]: {
            primary: urlResolution.primary,
            fallbacks: urlResolution.fallbacks,
            current: urlResolution.primary,
            attemptIndex: 0
          }
        }));
        
        // Reset fallback attempts
        setFallbackAttempts(prev => ({ ...prev, [imageId]: 0 }));
        
        console.log(`Retrying image ${imageId} with cache-busted URL:`, urlResolution.primary);
        toast.success(`Retrying ${image.filename || image.original_filename}...`);
      }
    } catch (error) {
      console.error('Retry failed:', error);
      toast.error(`Failed to reload ${image.filename || image.original_filename}`);
      setErrorStates(prev => ({ ...prev, [imageId]: true }));
      setLoadingStates(prev => ({ ...prev, [imageId]: false }));
    }
  };

  if (!images || images.length === 0) {
    return (
      <div className={`text-center py-8 ${className}`}>
        <div className="flex flex-col items-center text-gray-400">
          <ImageIcon size={48} className="mb-3" />
          <p className="text-sm font-medium">No images available</p>
          <p className="text-xs mt-1">Images will appear here once uploaded</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Preload Progress Indicator */}
      {preloadingImages && preloadProgress.total > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-blue-700 font-medium">Loading images...</span>
            <span className="text-xs text-blue-600">
              {preloadProgress.loaded}/{preloadProgress.total}
            </span>
          </div>
          <div className="w-full bg-blue-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ 
                width: `${(preloadProgress.loaded / preloadProgress.total) * 100}%` 
              }}
            ></div>
          </div>
        </div>
      )}

      {/* Image Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {images.map((image, index) => (
          <div key={image.id} className="relative group">
            {/* Image Container */}
            <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden border border-gray-200 hover:border-gray-300 transition-colors">
              {/* Loading State */}
              {loadingStates[image.id] && (
                <div className="w-full h-full flex flex-col items-center justify-center bg-gray-50">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-cyan-500 mb-2"></div>
                  <div className="text-xs text-gray-500 text-center px-2">
                    <div>Loading image...</div>
                    {fallbackAttempts[image.id] > 0 && (
                      <div className="text-orange-600 mt-1">
                        Trying fallback {fallbackAttempts[image.id] + 1}
                      </div>
                    )}
                    {currentUrls[image.id] && currentUrls[image.id].attemptIndex > 0 && (
                      <div className="text-blue-600 mt-1">
                        Attempt {currentUrls[image.id].attemptIndex + 1}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Error State */}
              {errorStates[image.id] && (
                <div className="w-full h-full flex flex-col items-center justify-center text-gray-400 p-2">
                  <AlertCircle size={20} className="mb-1 text-red-500" />
                  <span className="text-xs text-center text-red-600 mb-1">Failed to load</span>
                  {fallbackAttempts[image.id] > 0 && (
                    <span className="text-xs text-center text-orange-600 mb-2">
                      Tried {fallbackAttempts[image.id]} fallback{fallbackAttempts[image.id] !== 1 ? 's' : ''}
                    </span>
                  )}
                  {(retryAttempts[image.id] || 0) < 3 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRetryImage(image.id);
                      }}
                      className="flex items-center space-x-1 px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                      title="Retry loading image with cache-busting"
                    >
                      <RefreshCw size={12} />
                      <span>Retry</span>
                    </button>
                  )}
                  {(retryAttempts[image.id] || 0) >= 3 && (
                    <span className="text-xs text-red-500">Max retries reached</span>
                  )}
                </div>
              )}

              {/* Lazy Image */}
              {!loadingStates[image.id] && !errorStates[image.id] && currentUrls[image.id] && (
                <LazyImage
                  src={currentUrls[image.id].current}
                  thumbnailSrc={image.thumbnail?.dataUrl || image.thumbnail_url}
                  alt={image.filename || image.original_filename || 'Asset image'}
                  className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform duration-200"
                  onLoad={(event) => {
                    handleImageLoad(image.id);
                    const urlData = currentUrls[image.id];
                    const attemptInfo = urlData ? `(attempt ${urlData.attemptIndex + 1})` : '';
                    console.log(`Image loaded successfully for ${image.filename} ${attemptInfo}:`, urlData?.current);
                    if (event.cached) {
                      console.log(`Image loaded from cache: ${image.filename}`);
                    }
                  }}
                  onError={(event) => {
                    handleImageError(image.id, event);
                    const urlData = currentUrls[image.id];
                    const attemptInfo = urlData ? `(attempt ${urlData.attemptIndex + 1})` : '';
                    console.error(`Failed to load image: ${image.filename} ${attemptInfo}`, event.error);
                  }}
                  onClick={() => handleViewImage(index)}
                  cacheOptions={{ width: 300, height: 300 }} // Cache thumbnail size
                  key={`${image.id}-${currentUrls[image.id]?.attemptIndex || 0}`} // Force re-render on URL change
                  placeholder={
                    <div className="w-full h-full flex flex-col items-center justify-center bg-gray-50">
                      <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-300 border-t-cyan-500 mb-1"></div>
                      <div className="text-xs text-gray-500">Loading...</div>
                    </div>
                  }
                />
              )}

              {/* Hover Overlay */}
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-200 flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex space-x-2">
                  {/* View Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewImage(index);
                    }}
                    className="bg-white bg-opacity-90 text-gray-700 p-2 rounded-full hover:bg-opacity-100 transition-colors"
                    title="View full size"
                  >
                    <Eye size={16} />
                  </button>

                  {/* Remove Button */}
                  {editable && onImageRemove && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveClick(image.id, image.filename || image.original_filename);
                      }}
                      className="bg-red-600 bg-opacity-90 text-white p-2 rounded-full hover:bg-opacity-100 transition-colors"
                      title="Remove image"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </div>

              {/* Status Indicators */}
              <div className="absolute top-2 right-2 flex flex-col space-y-1">
                {/* Temporary/New indicator */}
                {image.temporary && (
                  <span className="text-xs text-orange-600 bg-orange-100 bg-opacity-90 px-1.5 py-0.5 rounded-full">
                    New
                  </span>
                )}

                {/* Processing indicator */}
                {image.processing && (
                  <span className="text-xs text-blue-600 bg-blue-100 bg-opacity-90 px-1.5 py-0.5 rounded-full">
                    Processing
                  </span>
                )}
              </div>
            </div>

            {/* Image Info */}
            <div className="mt-2 px-1">
              <p
                className="text-xs text-gray-600 truncate"
                title={image.filename || image.original_filename}
              >
                {image.filename || image.original_filename || 'Untitled'}
              </p>
              <div className="flex justify-between items-center mt-1">
                <span className="text-xs text-gray-400">
                  {image.size ? `${(image.size / (1024 * 1024)).toFixed(2)} MB` : ''}
                </span>
                {image.uploaded_at && (
                  <span className="text-xs text-gray-400">
                    {new Date(image.uploaded_at).toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Image Count */}
      <div className="text-center">
        <span className="text-sm text-gray-500">
          {images.length} image{images.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <AlertCircle size={24} className="text-red-600 mr-3" />
              <h3 className="text-lg font-semibold text-gray-900">Remove Image</h3>
            </div>

            <p className="text-gray-600 mb-6">
              Are you sure you want to remove "{showDeleteConfirm.name}"? This action cannot be undone.
            </p>

            <div className="flex space-x-3">
              <button
                onClick={cancelRemove}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmRemove}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssetImagePreview;